/* eslint-disable no-nested-ternary */
import { StockInfo } from '../StockInfo/StockInfo.component';
import { StockVariation } from '../StockVariation/StockVariation.component';
import { StockDetailsWrapper, StockDetailsWrapperProps } from './StockDetailsWrapper.component';
import { Product, ProductCategoryCode, Quote, UserProductType } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, forwardRef } from 'react';
import { getSalesRate } from '@stocks/business/Stocks.utils';
import { formatDateCompact, formatShortMonthWithYear } from '@utils/date';
import { formatNumberWithComma } from '@utils/number';
import classnames from 'classnames';
import styles from './StockDetails.module.scss';

type InheritedProps = ComponentPropsWithRef<'div'> & Pick<StockDetailsWrapperProps, 'cardType'>;

interface Props extends InheritedProps {
  productType: UserProductType;
  onDelete?: (id: number, marketId?: number) => void;
}

const STOCK_TERM_MARKET_NAME = 'Euronext';

function buildSubTitle(productType: UserProductType) {
  const { marketReference, name, productCategoryCode } = productType;

  if (productCategoryCode === ProductCategoryCode.Cattle) {
    return name;
  }
  if (productCategoryCode === ProductCategoryCode.CommoditiesFutures) {
    return STOCK_TERM_MARKET_NAME;
  }
  if (marketReference) {
    return marketReference.marketName;
  }
  return undefined;
}

function getProduct(productType: UserProductType) {
  // const { marketReference, products } = productType;
  // return products.find(p => marketReference && p.market?.id === marketReference.id) || products[0];
  const { products, productCategoryCode, availableDates, marketReference } = productType;
  const isTerm = productCategoryCode === ProductCategoryCode.CommoditiesFutures;
  const isCrops = productCategoryCode === ProductCategoryCode.Crops;

  let product: Product;

  // Seul crops (récolte) a un marché de référence.
  if (isCrops) {
    product =
      (products.find((item) => marketReference && item.market?.marketName === marketReference.marketName) as Product) ||
      products[0];
  } else if (isTerm) {
    product = products[0] as Product;
  } else {
    const mostRecenteDate = availableDates[0];
    product =
      (products.find((item) => mostRecenteDate && item.quote.date === mostRecenteDate) as Product) || products[0];
  }

  return product;
}

/**
 * Etant donné que l'attribut _var déconne on est obligé d'utiliser cette méthode (pas trouvé mieux)
 * pour extraire la valeur de var.
 * @param quote
 * @returns amount
 */
function extractVarFromQuote(quote: Quote) {
  // On itère sur les keys value de l'objet pour récupérer seulement [["var"], ["sa valeur"]]
  const quoteAttributUnderArray2D = Object.entries(quote).filter((entry) => {
    return entry[0] === 'var';
  });
  if (quoteAttributUnderArray2D && quoteAttributUnderArray2D[0].length) {
    return parseFloat(quoteAttributUnderArray2D[0][1]);
  }
  return 0;

  // // On transforme en tab de 1 dim
  // const quoteAttributUnderArray1D = quoteAttributUnderArray2D.join().split(',');
  // // On ne récupère que le dernier élément du tableau et on cast en number sinon 0
  // return quoteAttributUnderArray1D.length === 1 ? Number(quoteAttributUnderArray1D.splice(1, 1)) : 0;
}

export function buildContent(
  productType: UserProductType,
  cardType: Props['cardType'],
  isTerm: boolean,
  isFavorite: boolean
) {
  const { productCategoryCode, cattleAmount } = productType;
  const product = getProduct(productType);
  const { quote, unit } = product ?? {};
  const { variation1d, variation12m, price, tax, date } = quote
    ? quote
    : { date: undefined, price: 0, tax: 0, variation12m: 0, variation1d: 0 };

  const subtitle = cardType === 'full' || isTerm ? buildSubTitle(productType) : '-';
  const type = cardType === 'full' ? 'favorite' : 'journal';
  const contentItemClassName = 'plc-mr-m';
  const containerClassName = classnames(
    styles.gridContainer,
    productCategoryCode === ProductCategoryCode.Pork || productCategoryCode === ProductCategoryCode.Energies
      ? styles.twoRows
      : styles.threeRows,
    isFavorite ? styles.favorite : ''
  );
  switch (productCategoryCode) {
    case ProductCategoryCode.Crops:
    case ProductCategoryCode.CattleFood: {
      return (
        <div className={containerClassName}>
          <StockInfo
            className={classnames(contentItemClassName, styles.top)}
            i18nTitle="stocks.reference-market"
            type={type}
          >
            {subtitle}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.middleLeft)}
            i18nTitle="stocks.date"
            type={type}
          >
            {formatDateCompact(date)}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.middleRight)}
            i18nTitle="stocks.tax"
            type={type}
            unit={unit}
          >
            {tax}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomLeft)}
            i18nTitle="stocks.average-price"
            type={type}
            unit={unit}
          >
            {formatNumberWithComma(price, 2)}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomRight)}
            i18nTitle="stocks.variation"
            type={type}
            unit={unit}
          >
            <StockVariation amount={variation1d} textProps={{ variant: 'h4' }} />
          </StockInfo>
        </div>
      );
    }
    case ProductCategoryCode.Energies:
    case ProductCategoryCode.Pork: {
      return (
        <div className={containerClassName}>
          <StockInfo
            className={classnames(contentItemClassName, styles.middleLeft)}
            i18nTitle="stocks.date"
            type={type}
          >
            {formatDateCompact(date)}
          </StockInfo>
          {productCategoryCode === ProductCategoryCode.Pork && (
            <StockInfo
              className={classnames(contentItemClassName, styles.middleRight)}
              i18nTitle="stocks.long-term-variation"
              type={type}
              unit={unit}
            >
              <StockVariation amount={variation12m} textProps={{ variant: 'h4' }} />
            </StockInfo>
          )}
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomLeft)}
            i18nTitle="stocks.average-price"
            type={type}
            unit={unit}
          >
            {formatNumberWithComma(price, 2)}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomRight)}
            i18nTitle="stocks.variation"
            type={type}
            unit={unit}
          >
            <StockVariation amount={variation1d} textProps={{ variant: 'h4' }} />
          </StockInfo>
        </div>
      );
    }
    case ProductCategoryCode.Cattle: {
      if (!cattleAmount) {
        return null;
      }
      const { sold, salesRate } = getSalesRate(cattleAmount.sum);
      return (
        <div className={containerClassName}>
          <StockInfo
            className={classnames(contentItemClassName, styles.top)}
            i18nTitle="stocks.reference-market"
            type={type}
          >
            {subtitle}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.middleLeft)}
            i18nTitle="stocks.date"
            type={type}
          >
            {formatDateCompact(date)}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomLeft)}
            i18nTitle="stocks.headcount"
            type={type}
          >
            {formatNumberWithComma(sold, 2)}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomRight)}
            i18nTitle="stocks.sale-rate"
            type={type}
          >{`${formatNumberWithComma(salesRate, 2)}%`}</StockInfo>
        </div>
      );
    }
    case ProductCategoryCode.CommoditiesFutures: {
      const amount = extractVarFromQuote(quote);
      return (
        <div className={containerClassName}>
          <StockInfo
            className={classnames(contentItemClassName, styles.top)}
            i18nTitle="stocks.reference-market"
            type={type}
          >
            {subtitle}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.middleLeft)}
            i18nTitle="stocks.date"
            type={type}
          >
            {formatDateCompact(date)}
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomLeft)}
            i18nTitle="stocks.variation"
            type={type}
            unit={unit}
          >
            <StockVariation amount={amount} textProps={{ variant: 'h4' }} />
          </StockInfo>
          <StockInfo
            className={classnames(contentItemClassName, styles.bottomRight)}
            i18nTitle="stocks.term.table.header.last-quote"
            type={type}
            unit={unit}
          >
            {formatNumberWithComma(price, 2)}
          </StockInfo>
        </div>
      );
    }
    default: {
      const switchTest: never = productCategoryCode;
      return switchTest;
    }
  }
}

const StockDetails: FC<Props> = forwardRef(({ productType, className, cardType, ...rest }, stockDetailRef) => {
  const rootClass = classnames(
    styles['stock-details'],
    'plc-p-m',
    'flex',
    'column',
    'background-ultralight',
    cardType,
    className
  );
  const { name, productCategoryCode } = productType;
  const isCattle = productCategoryCode === ProductCategoryCode.Cattle;
  const isTerm = productCategoryCode === ProductCategoryCode.CommoditiesFutures;

  const product = getProduct(productType);
  const market = product?.market ?? productType?.marketReference;
  const deliveryDate = product?.deliveryDate;

  const title = isCattle
    ? market
      ? market.marketName
      : '-'
    : isTerm && deliveryDate
      ? `${name} - ${formatShortMonthWithYear(deliveryDate)}`
      : name;

  return (
    <StockDetailsWrapper
      ref={stockDetailRef}
      cardType={cardType}
      className={rootClass}
      productType={productType}
      title={title}
      {...rest}
    >
      {buildContent(productType, cardType, isTerm, true)}
    </StockDetailsWrapper>
  );
});

export { StockDetails };
